/* KycSectionPepWithScreening.jsx */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

import Lottie from "lottie-react";
import pepAnim from "../../../images/pep-lottie.json";
import overallAlertAnim from "../../../images/alert-lottie.json";
import overallClearedAnim from "../../../images/cleared-lottie.json";
import overallPendingAnim from "../../../images/pending-lottie.json";
import handFingerUpIcon from "../../../images/hand-fingerup.svg";
import doubleTapFullIcon from "../../../images/double-tap-full.svg";
import userIcon from "../../../images/user.svg";
import userGreenIcon from "../../../images/user-d-green.svg";
import userRedIcon from "../../../images/user-d-red4.svg";
import userYellowIcon from "../../../images/user-d-yellow2.svg";
import { trackScreeningUsage } from "../../../utils/screeningUsage";
import { MYKYC_API_BASE } from "../../../config/api";

import "../css/KycSectionPepSanctionWithScreening.css";

/**
 * Staff-only PEP section for KycFormPage.
 *
 * Screens:
 *  - Always: Client name
 *  - If client is an Entity: also screens Ownership names:
 *      1) Direct shareholders
 *      2) UBOs
 *      3) Authorised signatories (from ownership.controllers)
 *
 * UI:
 *  - Each match line shows the ROLE(S) of the screened subject (Client / Direct shareholder / UBO / Authorised signatory)
 *  - Also shows the screened subject name that triggered the match.
 *
 * Saves to:
 *   pep: {
 *     clientDeclared: boolean,
 *     clientDetails: string,
 *     staffDecision: "no_exposure" | "flagged" | "flagged_remediated" | "flagged_confirmed" | null,
 *     staffAssessment: string,
 *   }
 *
 *   internalUse: {
 *     pepScreening: {
 *       searchedName: string,             // legacy (kept)
 *       searchedSubjects: Array<{         // ✅ new
 *          key: string,
 *          name: string,
 *          roles: string[],
 *          meta?: object
 *       }>,
 *       subjectSignature: string,         // ✅ used to detect changes
 *       result: {
 *         subjects: Array<{
 *            key: string,
 *            name: string,
 *            roles: string[],
 *            meta?: object,
 *            response: <API payload>      // { results: [...] }
 *         }>,
 *         errors: Array<{ key, name, message }>
 *       },
 *       selectedKeys: string[],
 *       selectedMatches: object[],
 *       selectionSaved: boolean,          // legacy (still supported)
 *       reviewStatus: "draft" | "completed" | null,
 *     }
 *   }
 */

function safeStr(v) {
  return String(v == null ? "" : v);
}
function clean(v) {
  return safeStr(v)
    .replace(/\uFEFF/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^"(.*)"$/, "$1");
}
function autoResizeTextarea(el) {
  if (!el || typeof window === "undefined") return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight + verticalExtras;
  const maxHeight = lineHeight * 25 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}
function normKey(s) {
  return clean(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
}
function asList(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    return v
      .split(/;|\|/g)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
}
function takeFirst(arr) {
  return Array.isArray(arr) && arr.length ? clean(arr[0]) : "";
}
function joinAll(v, sep = " · ") {
  return asList(v).map(clean).filter(Boolean).join(sep);
}
function clampText(s, max = 120) {
  const t = clean(s);
  if (!t) return "";
  return t.length > max ? t.slice(0, max) + "…" : t;
}

function toTitleCaseWords(value) {
  return clean(value)
    .toLowerCase()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function pluralize(n, singular, plural) {
  return n === 1 ? singular : plural;
}

function screenedRolesText(subjects) {
  const arr = Array.isArray(subjects) ? subjects : [];
  const counts = {
    Client: 0,
    "Direct shareholder": 0,
    UBO: 0,
    "Authorised signatory": 0,
  };

  for (const s of arr) {
    const roles = Array.isArray(s?.roles) ? s.roles : [];
    const uniq = Array.from(
      new Set(roles.map((r) => clean(r)).filter(Boolean))
    );
    const lower = uniq.map((r) => r.toLowerCase());
    if (lower.some((r) => r.includes("client"))) counts.Client += 1;
    else if (lower.some((r) => r.includes("direct shareholder")))
      counts["Direct shareholder"] += 1;
    else if (
      lower.some(
        (r) =>
          r === "ubo" ||
          r.includes("ubo") ||
          r === "settlor" ||
          r === "trustee" ||
          r === "beneficiary"
      )
    )
      counts.UBO += 1;
    else if (lower.some((r) => r.includes("authorised signatory")))
      counts["Authorised signatory"] += 1;
  }

  const parts = [];
  if (counts.Client)
    parts.push(counts.Client === 1 ? "Client" : `${counts.Client} clients`);

  if (counts["Direct shareholder"])
    parts.push(
      `${counts["Direct shareholder"]} ${pluralize(
        counts["Direct shareholder"],
        "direct shareholder",
        "direct shareholders"
      )}`
    );

  if (counts.UBO)
    parts.push(`${counts.UBO} ${pluralize(counts.UBO, "UBO", "UBOs")}`);

  if (counts["Authorised signatory"])
    parts.push(
      `${counts["Authorised signatory"]} ${pluralize(
        counts["Authorised signatory"],
        "authorised signatory",
        "authorised signatories"
      )}`
    );

  return parts.join(", ") || "Client";
}

function badge(text, scheme = "secondary") {
  const cls =
    scheme === "danger"
      ? "badge badgeStandard badgeKycRed"
      : scheme === "warning"
      ? "badge badgeStandard badgeKycYellow"
      : scheme === "success"
      ? "badge badgeStandard badgeKycGreen"
      : scheme === "grey"
      ? "badge badgeStandard badgeKycGrey"
      : scheme === "orange"
      ? "badge badgeStandard badgeKycOrange"
      : "badge badgeStandard badgeKycGrey";
  return <span className={cls}>{text}</span>;
}

/** ✅ squared role badge using your CSS classes */
function roleBadge(role) {
  const r = clean(role);
  if (!r) return null;
  return (
    <span
      className="squareBadge squareBadgeDarkBlue-outline"
      title="Screened role"
    >
      {r}
    </span>
  );
}

function strengthFromScore(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return "weak";
  if (s >= 0.9) return "strong";
  if (s >= 0.75) return "possible";
  return "weak";
}
function scorePill(strength) {
  if (strength === "strong") return badge("Strong", "danger");
  if (strength === "possible") return badge("Possible", "warning");
  return badge("Weak", "secondary");
}

function pepWebCategoryBadge(category) {
  const v = clean(category).toLowerCase();
  if (v === "pep") return badge("PEP", "grey");
  if (v === "pep_related" || v === "close_associate")
    return badge("Close to PEP", "grey");
  return badge("Not PEP", "grey");
}

function collectOpenSanctionsTopics(value, out = []) {
  if (!value) return out;
  if (typeof value === "string") {
    if (value) out.push(value);
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectOpenSanctionsTopics(item, out));
    return out;
  }
  if (typeof value === "object") {
    if (value.topics) collectOpenSanctionsTopics(value.topics, out);
    if (value.properties?.topics) collectOpenSanctionsTopics(value.properties.topics, out);
    if (value.entity?.topics) collectOpenSanctionsTopics(value.entity.topics, out);
    if (value.entity?.properties?.topics)
      collectOpenSanctionsTopics(value.entity.properties.topics, out);
  }
  return out;
}

function pepHitClassification(record) {
  const saved = record?.pepClassification || null;
  if (saved?.code) return saved;

  const topics = Array.from(
    new Set(
      collectOpenSanctionsTopics(record)
        .map((topic) => String(topic).trim())
        .filter(Boolean)
    )
  );
  if (topics.includes("role.pep")) {
    return { code: "direct_pep", label: "PEP", topics };
  }
  if (topics.includes("role.rca")) {
    return {
      code: "rca",
      label: "RCA / family member or close associate of a PEP",
      topics,
    };
  }
  return { code: "unknown", label: "PEP/RCA match", topics };
}

function pepHitClassificationBadge(classification) {
  const code = clean(classification?.code).toLowerCase();
  const sharedStyle = {
    background: "#AAACB2",
    color: "#fff",
  };
  if (code === "direct_pep") {
    return (
      <span className="badge badgeStandard" style={sharedStyle} title={classification?.label || "PEP"}>
        PEP
      </span>
    );
  }
  if (code === "rca") {
    return (
      <span
        className="badge badgeStandard"
        style={sharedStyle}
        title={classification?.label || "RCA / family member or close associate of a PEP"}
      >
        Close to PEP
      </span>
    );
  }
  return <span title={classification?.label || "PEP/RCA match"}>{badge("PEP/RCA", "grey")}</span>;
}

function isPepWebCategoryRelevant(category) {
  const v = clean(category).toLowerCase();
  return v === "pep" || v === "pep_related" || v === "close_associate";
}

function pepWebContextMatchBadge(outcome) {
  const v = clean(outcome).toLowerCase();
  if (v === "likely_match") return badge("Likely same person", "danger");
  if (v === "possible_match")
    return badge("Possibly same person", "warning");
  if (v === "likely_false_positive")
    return badge("Likely false positive", "success");
  return badge("Insufficient information", "grey");
}

function pepWebContextIcon(outcome) {
  const v = clean(outcome).toLowerCase();
  if (v === "likely_match") return userRedIcon;
  if (v === "possible_match") return userYellowIcon;
  if (v === "likely_false_positive") return userGreenIcon;
  return userIcon;
}

function defaultPepContextAssessment() {
  return {
    code: "no_context",
    label: "No context",
    checks: {
      nationality: "not_comparable",
      domicile: "not_comparable",
      age: "not_comparable",
    },
  };
}

function pepContextBadgeScheme(code) {
  if (code === "context_supports") return "danger";
  if (code === "context_mixed") return "warning";
  if (code === "context_conflicts") return "success";
  return "grey";
}

function pepContextBadgeLabel(context) {
  const code = clean(context?.code);
  if (code === "context_supports") return "Likely same person";
  if (code === "context_mixed") return "Possibly same person";
  if (code === "context_conflicts") return "Likely false positive";
  return "Context unclear";
}

function pepContextBadge(context) {
  const code = clean(context?.code);
  return badge(pepContextBadgeLabel(context), pepContextBadgeScheme(code));
}

function overallPepMatchAssessment(strength, context) {
  const s = clean(strength).toLowerCase();
  const code = clean(context?.code);
  if (code === "context_conflicts") {
    return { label: "Likely false positive", scheme: "success" };
  }
  if (code === "context_mixed") {
    return { label: "Possibly same person", scheme: "warning" };
  }
  if (code === "context_supports") {
    return s === "strong"
      ? { label: "Likely same person", scheme: "danger" }
      : { label: "Possibly same person", scheme: "warning" };
  }
  return s === "strong"
    ? { label: "Possibly same person", scheme: "warning" }
    : { label: "Unclear", scheme: "grey" };
}

function overallPepMatchBadge(strength, context) {
  const overall = overallPepMatchAssessment(strength, context);
  return badge(overall.label, overall.scheme);
}

function formatPepContextCheck(value) {
  const v = clean(value);
  if (!v || v === "not_comparable") return "N/A";
  return v.replace(/_/g, " ");
}

function pepContextAssessmentLine(context) {
  const checks = context?.checks || {};
  const conflictLabels = [
    checks.nationality === "conflict" ? "nationality" : "",
    checks.age === "conflict" ? "date of birth" : "",
  ].filter(Boolean);

  if (context?.code === "context_supports") {
    return "The available identity context supports this match.";
  }
  if (context?.code === "context_mixed") {
    return "The available identity context is mixed and should be reviewed manually.";
  }
  if (context?.code === "context_conflicts" && conflictLabels.length) {
    return `The available identity context does not align on ${conflictLabels.join(
      ", "
    )}. It is likely a different person.`;
  }
  if (context?.code === "context_conflicts") {
    return "The available identity context does not align. It is likely a different person.";
  }
  return "No comparable identity context is available.";
}

function PepContextCheckChip({ label, value }) {
  return (
    <span
      className="d-inline-flex align-items-center"
      style={{
        gap: 5,
        minHeight: 24,
        padding: "3px 8px",
        border: "1px solid #DED8D6",
        borderRadius: 6,
        background: "#EEEAE9",
        color: "#3F4652",
        fontSize: 11,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: "#667085" }}>{label}:</span>
      <span>{formatPepContextCheck(value)}</span>
    </span>
  );
}

function pepSignalFromAiCategory(category) {
  const v = clean(category).toLowerCase();
  if (v === "pep") return "pep";
  if (v === "pep_related" || v === "close_associate") return "close";
  return "";
}

function pepSignalFromMatchClassification(classification) {
  const code = clean(classification?.code).toLowerCase();
  if (code === "direct_pep") return "pep";
  if (code === "rca") return "close";
  return "";
}

function pepSignalBadge(label) {
  if (!label) return null;
  return (
    <span
      className="badge badgeStandard"
      style={{ background: "#AAACB2", color: "#fff" }}
    >
      {label}
    </span>
  );
}

function consolidatedPepSignalLabel(aiItem, matches) {
  const signals = new Set();
  const aiSignal = pepSignalFromAiCategory(aiItem?.result?.category);
  if (aiSignal) signals.add(aiSignal);
  (matches || []).forEach((match) => {
    const signal = pepSignalFromMatchClassification(match?.classification);
    if (signal) signals.add(signal);
  });

  if (signals.has("pep") && signals.has("close")) return "PEP / Close to PEP";
  if (signals.has("pep")) return "PEP";
  if (signals.has("close")) return "Close to PEP";
  if ((matches || []).length) return "PEP/RCA";
  return "";
}

function labelFromMatchType(matchObj) {
  const raw = matchObj ? JSON.stringify(matchObj) : "";
  const s = clean(raw).toLowerCase();
  if (!s) return "Potential match";

  if (s.includes("name_exact") || s.includes("exact")) return "Exact name";
  if (s.includes("alias")) return "Alias";
  if (s.includes("fuzzy")) return "Similar spelling";
  if (s.includes("token") || s.includes("overlap")) return "Similar words";
  return "Potential match";
}

// background color of the cards above the hits
function toneStyles({ hasResult, hasMatches }) {
  if (!hasResult) return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4C5465" };
  if (!hasMatches) return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4D5465" };
  return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4D5465" };
}

// EDE8E5 EDEAE9

// function toneStyles({ hasResult, hasMatches }) {
//   if (!hasResult) return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4C5465" };
//   if (!hasMatches) return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4D5465" };
//   return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4D5465" };
// }

function renderPepDetailsFromProperties(properties) {
  const p = properties || {};
  const pick = (k) => (Array.isArray(p?.[k]) ? p[k].filter(Boolean) : []);

  const lines = [];
  const names = pick("name");
  const firstName = pick("firstName");
  const lastName = pick("lastName");
  const position = pick("position");
  const birthDate = pick("birthDate");
  const alias = pick("alias");
  const notes = pick("notes");

  if (names.length) lines.push({ k: "Names", v: names.join(" · ") });
  if (firstName.length) lines.push({ k: "First name", v: firstName.join(" ") });
  if (lastName.length) lines.push({ k: "Last name", v: lastName.join(" ") });

  if (birthDate.length)
    lines.push({ k: "Birth date", v: birthDate.join(", ") });
  if (position.length) lines.push({ k: "Position", v: position.join(" · ") });
  if (notes.length) lines.push({ k: "Notes", v: notes.join(" · ") });

  if (alias.length)
    lines.push({
      k: "Alias",
      v:
        alias.slice(0, 8).join(" · ") +
        (alias.length > 8 ? ` (+${alias.length - 8} more)` : ""),
    });

  if (!lines.length) {
    return (
      <div className="small text-muted">No additional details available.</div>
    );
  }

  return (
    <div className="small">
      {lines.map((x, i) => (
        <div key={i} className="mb-1">
          <span className="text-muted">{x.k}:</span>{" "}
          <span style={{ color: "inherit" }}>{x.v}</span>
        </div>
      ))}
    </div>
  );
}

function summarizePepSnapshotFromRecord(record) {
  const props = record?.properties || record?.entity?.properties || {};
  const birthDate = takeFirst(props?.birthDate);
  const position = joinAll(props?.position, " · ");
  const notes = takeFirst(props?.notes);
  return { position, notes, birthDate };
}

function looksLikeEntityCustomer(form) {
  const t = clean(
    form?.customer?.type ||
      form?.customer?.customerType ||
      form?.customer?.clientType ||
      form?.customer?.personType ||
      form?.customer?.category ||
      ""
  ).toLowerCase();

  if (t.includes("entity") || t.includes("company") || t.includes("corporate"))
    return true;
  if (t.includes("individual") || t.includes("person")) return false;

  const sh = Array.isArray(form?.ownership?.shareholders)
    ? form.ownership.shareholders
    : [];
  const ub = Array.isArray(form?.ownership?.ubos) ? form.ownership.ubos : [];
  const ct = Array.isArray(form?.ownership?.signatories)
    ? form.ownership.signatories
    : [];

  const hasOwnershipNames =
    sh.some((x) => clean(x?.name)) ||
    ub.some((x) => clean(x?.name)) ||
    ct.some((x) => clean(x?.name));

  const hasEntityShareholder = sh.some(
    (x) => clean(x?.holderType).toLowerCase() === "entity"
  );

  return hasOwnershipNames || hasEntityShareholder;
}

function buildPepSubjects(form) {
  const subjects = [];
  const customer = form?.customer || {};
  const customerIsEntity = looksLikeEntityCustomer(form);

  const customerName = clean(form?.customer?.name);
  if (customerName) {
    subjects.push({
      key: `client:${normKey(customerName)}`,
      name: customerName,
      roles: ["Client"],
      meta: {
        subjectType: customerIsEntity ? "entity" : "individual",
        nationality: clean(customer?.nationalityOrIncorporationCountry || ""),
        domicileCountry: clean(customer?.country || ""),
        dateOfBirth: customer?.dobOrIncorporationDate || "",
        age: computeAgeFromDob(customer?.dobOrIncorporationDate),
      },
    });
  }

  const isEntity = customerIsEntity;
  if (!isEntity) return { isEntity: false, subjects };

  const ownership = form?.ownership || {};
  const shareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : [];
  const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
  const controllers = Array.isArray(ownership?.signatories)
    ? ownership.signatories
    : [];
  const legalArrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];

  const add = (name, role, meta) => {
    const nm = clean(name);
    if (!nm) return;
    subjects.push({
      key: `${role.toLowerCase().replace(/\s+/g, "_")}:${normKey(nm)}`,
      name: nm,
      roles: [role],
      meta: meta || {},
    });
  };

  shareholders.forEach((s, idx) => {
    const nm = clean(s?.name);
    if (!nm) return;
    const holderType = clean(s?.holderType).toLowerCase();
    add(nm, "Direct shareholder", {
      idx,
      subjectType:
        holderType === "entity" ||
        holderType === "company" ||
        holderType === "trust" ||
        holderType === "foundation"
          ? "entity"
          : "individual",
      holderType: clean(s?.holderType),
      beneficialOwnerType: clean(s?.beneficialOwnerType),
      ownershipPercent: clean(s?.ownershipPercent),
      nationality: clean(s?.nationality || ""),
      domicileCountry: clean(s?.countryOfResidence || ""),
      dateOfBirth: s?.dateOfBirth || s?.dob || "",
    });
  });

  ubos.forEach((u, idx) => {
    const nm = clean(u?.name);
    if (!nm) return;
    add(nm, "UBO", {
      idx,
      subjectType: "individual",
      basisOfControl: clean(u?.basisOfControl),
      relatedShareholderIdx: safeStr(u?.relatedShareholderIdx ?? ""),
      nationality: clean(u?.nationality || ""),
      domicileCountry: clean(u?.countryOfResidence || ""),
      dateOfBirth: u?.dateOfBirth || u?.dob || "",
    });
  });

  controllers.forEach((c, idx) => {
    const nm = clean(c?.name);
    if (!nm) return;
    add(nm, "Authorised signatory", {
      idx,
      subjectType: "individual",
      capacity: clean(c?.capacity),
      nationality: clean(c?.nationality || ""),
      domicileCountry: clean(c?.countryOfResidence || ""),
      dateOfBirth: c?.dateOfBirth || c?.dob || "",
    });
  });

  const accountHolderTrust = legalArrangements.find((arr) => {
    const type = clean(arr?.arrangementType || arr?.type).toLowerCase();
    const source = clean(arr?.source).toLowerCase();
    return type === "trust" && source === "account_holder";
  });

  if (accountHolderTrust) {
    const parties = Array.isArray(accountHolderTrust?.parties)
      ? accountHolderTrust.parties
      : [];

    parties.forEach((p, idx) => {
      const nm = clean(p?.name);
      const role = clean(p?.role).toLowerCase();
      if (!nm || !role) return;
      if (/^class\b/i.test(nm) || /\bunnamed\b/i.test(nm)) return;

      if (role === "trustee") {
        const trusteeKind = clean(p?.trusteeKind).toLowerCase();
        if (trusteeKind === "company" || trusteeKind === "corporate") return;
        add(nm, "Trustee", {
          idx: `trustee_${idx}`,
          subjectType: "individual",
          nationality: clean(p?.nationality || ""),
          domicileCountry: clean(p?.domicile || ""),
          dateOfBirth: p?.dateOfBirth || p?.dob || "",
        });
        return;
      }

      if (role === "settlor" || role === "beneficiary") {
        add(nm, role === "settlor" ? "Settlor" : "Beneficiary", {
          idx: `${role}_${idx}`,
          subjectType: "individual",
          nationality: clean(p?.nationality || ""),
          domicileCountry: clean(p?.domicile || ""),
          dateOfBirth: p?.dateOfBirth || p?.dob || "",
        });
      }
    });
  }

  const map = new Map();
  for (const s of subjects) {
    const n = clean(s.name).toLowerCase();
    if (!n) continue;
    const prev = map.get(n);
    if (!prev) {
      map.set(n, { ...s, roles: Array.from(new Set(s.roles)) });
    } else {
      const roles = new Set([...(prev.roles || []), ...(s.roles || [])]);
      map.set(n, {
        ...prev,
        roles: Array.from(roles),
        meta:
          prev.meta && s.meta
            ? { ...(prev.meta || {}), ...(s.meta || {}) }
            : prev.meta || s.meta || {},
      });
    }
  }

  const out = Array.from(map.values());
  out.sort((a, b) => {
    const aClient = a.roles?.includes("Client") ? 0 : 1;
    const bClient = b.roles?.includes("Client") ? 0 : 1;
    if (aClient !== bClient) return aClient - bClient;
    return a.name.localeCompare(b.name);
  });

  return { isEntity: true, subjects: out };
}

function buildSubjectsSignature(subjects) {
  return subjects
    .map(
      (s) =>
        `${clean(s.name).toLowerCase()}|${(s.roles || [])
          .slice()
          .sort()
          .join(",")}`
    )
    .join("||");
}

async function fetchPepForName(name, { limit, threshold }) {
  const res = await fetch(
    `${MYKYC_API_BASE}/myKyc/screening/opensanctions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        limit: limit ?? 12,
        threshold: threshold ?? 0.7,
      }),
    }
  );

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) {
    const msg = json?.error || json?.message || "Screening failed";
    const err = new Error(msg);
    err._status = res.status;
    err._json = json;
    throw err;
  }
  return json;
}

async function asyncPool(limit, items, worker) {
  const ret = [];
  const executing = [];
  for (const item of items) {
    const p = Promise.resolve().then(() => worker(item));
    ret.push(p);
    if (limit <= items.length) {
      const e = p.then(() => executing.splice(executing.indexOf(e), 1));
      executing.push(e);
      if (executing.length >= limit) await Promise.race(executing);
    }
  }
  return Promise.all(ret);
}

function computeAgeFromDob(value) {
  if (!value) return null;
  const d =
    value instanceof Date
      ? value
      : typeof value?.toDate === "function"
      ? value.toDate()
      : new Date(value);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

function buildScreeningUsageContext(form, screeningType) {
  return {
    orgId: clean(form?.orgId || form?.customer?.orgId || ""),
    clientId: clean(form?.clientId || form?.id || form?.customer?.clientId || ""),
    screeningType,
    source: "kyc",
  };
}

async function fetchPepWebAssessment({ name, nationality, domicileCountry, age, usageContext }) {
  const payload = {
    name,
    nationality,
    domicileCountry,
    age,
    screeningUsage: usageContext || {},
  };

  const res = await fetch(
    `${MYKYC_API_BASE}/myKyc/screening/pep_web_assessment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) {
    const msg = json?.error || json?.message || "PEP web assessment failed";
    const err = new Error(msg);
    err._status = res.status;
    err._json = json;
    throw err;
  }
  return json;
}

async function fetchPepContextAssessment({ subject, match }) {
  const fallback = defaultPepContextAssessment();
  try {
    const res = await fetch(`${MYKYC_API_BASE}/myKyc/screening/pep-context-matching`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        screenedSubject: {
          name: clean(subject?.name || ""),
          roles: Array.isArray(subject?.roles) ? subject.roles : [],
          meta: subject?.meta || {},
        },
        match: {
          source: "opensanctions",
          matchedName: match?.caption || match?.name || "",
          record: match || {},
        },
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.error) return fallback;
    return {
      code: clean(json?.code || fallback.code),
      label: clean(json?.label || fallback.label),
      checks: {
        nationality: clean(json?.checks?.nationality || fallback.checks.nationality),
        domicile: clean(json?.checks?.domicile || fallback.checks.domicile),
        age: clean(json?.checks?.age || fallback.checks.age),
      },
    };
  } catch {
    return fallback;
  }
}

/* ---------------- Framer Motion (minimal ROI) ---------------- */
const T = {
  fast: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
  med: { duration: 0.26, ease: [0.22, 1, 0.36, 1] },
};

const cardIn = {
  initial: { opacity: 0, y: 10, scale: 0.995 },
  animate: { opacity: 1, y: 0, scale: 1, transition: T.med },
  exit: { opacity: 0, y: 8, scale: 0.995, transition: T.fast },
};

const detailsIn = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: T.med },
  exit: { opacity: 0, height: 0, transition: T.fast },
};

// EXPERIMENTAL LAYOUT 2 (temporary): /kyc-bis-2 — classic arrows (narrow shaft, wide head) of the PEP section.
// Overview band unchanged; details render as aligned "extensions" of the three
// overview tiles on a continuous grey slab, with a screening band linking down
// to the full-width results card. Functional logic identical to the original.
const KycSectionPepBis2 = forwardRef(function KycSectionPepBis2({
  sectionNo = 0,
  form,
  onSetAt,
  setAt,
  changedClass,
  readOnly = false,
  clientLocked = true,
  initiallyExpanded = false,
}, ref) {
  const writeAt = typeof onSetAt === "function" ? onSetAt : setAt;

  const pep = form?.pep || {};
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const customerName = useMemo(
    () => clean(form?.customer?.name || ""),
    [form?.customer?.name]
  );
  const { isEntity, subjects: pepSubjects } = useMemo(
    () => buildPepSubjects(form),
    [form]
  );
  const pepWebAssessmentSubjects = useMemo(
    () =>
      (pepSubjects || []).filter(
        (s) => clean(s?.meta?.subjectType).toLowerCase() !== "entity"
      ),
    [pepSubjects]
  );
  const pepWebAssessmentSignature = useMemo(
    () =>
      pepWebAssessmentSubjects
        .map((s) =>
          [
            clean(s?.key),
            clean(s?.name).toLowerCase(),
            clean(s?.meta?.nationality).toUpperCase(),
            clean(s?.meta?.domicileCountry).toUpperCase(),
            s?.meta?.age ?? "",
          ].join("|")
        )
        .join("||"),
    [pepWebAssessmentSubjects]
  );
  const pepContextAssessmentSignature = useMemo(
    () =>
      (pepSubjects || [])
        .map((s) =>
          [
            clean(s?.key),
            clean(s?.name).toLowerCase(),
            clean(s?.meta?.nationality).toUpperCase(),
            clean(s?.meta?.dateOfBirth || ""),
            s?.meta?.age ?? "",
          ].join("|")
        )
        .join("||"),
    [pepSubjects]
  );

  const subjectSignature = useMemo(
    () => buildSubjectsSignature(pepSubjects),
    [pepSubjects]
  );

  // ---------------- PEP SCREENING LOCAL STATE ----------------
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [screeningErr, setScreeningErr] = useState("");
  const [screeningResult, setScreeningResult] = useState(null);

  const [selectedOnly, setSelectedOnly] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [draftKeys, setDraftKeys] = useState(() => new Set());
  const [pepReviewScrolled, setPepReviewScrolled] = useState(false);

  const hasResult = !!screeningResult;

  const [reviewStatus, setReviewStatus] = useState(null); // "draft" | "completed" | null
  const [pepWebAssessmentLoading, setPepWebAssessmentLoading] = useState(false);
  const [pepWebAssessmentErr, setPepWebAssessmentErr] = useState("");
  const [pepWebAssessment, setPepWebAssessment] = useState([]);
  const clientCommentsRef = useRef(null);
  const complianceCommentsRef = useRef(null);
  const visiblePepWebAssessment = useMemo(
    () =>
      (pepWebAssessment || []).filter((item) =>
        isPepWebCategoryRelevant(item?.result?.category)
      ),
    [pepWebAssessment]
  );

  function resetComplianceAssessment() {
    if (typeof writeAt !== "function") return;
    writeAt(["pep", "staffDecision"], null);
    writeAt(["pep", "staffAssessment"], "");
  }

  function persistScreeningState(partial) {
    if (typeof writeAt !== "function") return;
    // Merge over the saved doc: every caller passes its intent explicitly, so
    // this only protects fields a writer omits (full resets write null directly).
    const saved = form?.internalUse?.pepScreening || {};
    const next = {
      ...saved,
      searchedName: customerName || "",
      searchedSubjects: pepSubjects || [],
      subjectSignature: subjectSignature || "",
      ...(partial || {}),
    };
    // A transiently-null component state must never wipe the saved result —
    // that is what blanked the compliance toggles after hide/show details.
    if (next.result == null && saved.result != null) next.result = saved.result;
    writeAt(["internalUse", "pepScreening"], next);
  }

  function persistPepWebAssessment(partial) {
    if (typeof writeAt !== "function") return;
    writeAt(["internalUse", "pepWebAssessment"], partial || null);
  }

  // --- Toggle visibibility details section ---
  // Show/hide everything below the TOP SUMMARY CARD - true : visible, false: hidden
  const [showDetails, setShowDetails] = useState(!!initiallyExpanded);
  // The extension row is clipped while its height animates, then released so the
  // middle arrow can overhang into the screening card (deterministic, unlike
  // framer's transitionEnd which missed on re-expand).
  const [extRevealed, setExtRevealed] = useState(!!initiallyExpanded);
  const [complianceAssessmentGlowActive, setComplianceAssessmentGlowActive] =
    useState(false);
  const [
    complianceAssessmentIntroBackgroundActive,
    setComplianceAssessmentIntroBackgroundActive,
  ] = useState(false);
  const [actionRequiredBadgeVisible, setActionRequiredBadgeVisible] =
    useState(true);
  const actionRequiredBadgeRevealTimerRef = useRef(null);

  function toggleDetails() {
    setShowDetails((v) => !v);
  }

  function scrollPepAnchorThenCollapse() {
    const anchor = document.getElementById("kyc-sec-pep");
    anchor?.scrollIntoView({ behavior: "smooth", block: "start" });

    window.setTimeout(() => {
      setShowDetails(false);
    }, 500);
  }

  useEffect(() => {
    return () => {
      if (actionRequiredBadgeRevealTimerRef.current) {
        window.clearTimeout(actionRequiredBadgeRevealTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      autoResizeTextarea(complianceCommentsRef.current);
    });
  }, [pep?.staffAssessment]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      autoResizeTextarea(clientCommentsRef.current);
    });
  }, [pep?.clientDetails]);

  // ---------------- HYDRATE FROM SAVED KYC ----------------
  useEffect(() => {
    const saved = form?.internalUse?.pepScreening || null;

    const savedResult = saved?.result || null;
    const savedKeys = Array.isArray(saved?.selectedKeys)
      ? saved.selectedKeys
      : [];
    const savedSet = new Set(savedKeys);

    const rs = saved?.reviewStatus || null;
    setReviewStatus(rs);

    setScreeningResult(savedResult);
    setSelectedKeys(savedSet);
    setDraftKeys(new Set(savedSet));

    const shouldCollapse =
      rs === "draft" || rs === "completed" || !!saved?.selectionSaved;

    setSelectedOnly(shouldCollapse);
  }, [form?.internalUse?.pepScreening]);

  useEffect(() => {
    const saved = form?.internalUse?.pepWebAssessment || null;
    if (!saved) {
      setPepWebAssessment([]);
      setPepWebAssessmentErr("");
      setPepWebAssessmentLoading(false);
      return;
    }

    setPepWebAssessment(Array.isArray(saved?.items) ? saved.items : []);
    setPepWebAssessmentErr("");
    setPepWebAssessmentLoading(false);
  }, [form?.internalUse?.pepWebAssessment]);

  useEffect(() => {
    if (readOnly) return;
    if (!hasResult) return;
    if (!pepWebAssessmentSubjects.length) return;
    if (!pepWebAssessmentSignature) return;

    const saved = form?.internalUse?.pepWebAssessment || null;
    if (
      saved?.signature === pepWebAssessmentSignature &&
      Array.isArray(saved?.items) &&
      saved.items.length === pepWebAssessmentSubjects.length &&
      saved.items.every((item) => String(item?.result?.assessment || "").trim())
    ) {
      return;
    }

    let cancelled = false;

    async function run() {
      setPepWebAssessmentLoading(true);
      setPepWebAssessmentErr("");
      try {
        const items = [];
        for (const subj of pepWebAssessmentSubjects) {
          const json = await fetchPepWebAssessment({
            name: clean(subj?.name),
            nationality: clean(subj?.meta?.nationality || ""),
            domicileCountry: clean(subj?.meta?.domicileCountry || ""),
            age:
              subj?.meta?.age === null || subj?.meta?.age === undefined
                ? null
                : Number(subj.meta.age),
            usageContext: {
              ...buildScreeningUsageContext(form, "pep"),
              subjectName: clean(subj?.name),
              subjectKey: clean(subj?.key),
            },
          });

          items.push({
            subjectKey: clean(subj?.key),
            subjectName: clean(subj?.name),
            roles: Array.isArray(subj?.roles) ? subj.roles : [],
            query: json?.query || {
              name: clean(subj?.name),
              nationality: clean(subj?.meta?.nationality || ""),
              domicileCountry: clean(subj?.meta?.domicileCountry || ""),
              age:
                subj?.meta?.age === null || subj?.meta?.age === undefined
                  ? null
                  : Number(subj.meta.age),
            },
            result: json?.result || null,
          });
        }

        if (cancelled) return;

        const payload = {
          signature: pepWebAssessmentSignature,
          items,
          updatedAt: Date.now(),
        };

        setPepWebAssessment(items);
        persistPepWebAssessment(payload);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || "Failed to load PEP web assessment.";
        setPepWebAssessmentErr(msg);
        setPepWebAssessment([]);
      } finally {
        if (!cancelled) setPepWebAssessmentLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [
    readOnly,
    hasResult,
    pepWebAssessmentSubjects,
    pepWebAssessmentSignature,
    form?.internalUse?.pepWebAssessment,
  ]);

  const pepContextRefreshRef = useRef("");
  useEffect(() => {
    if (readOnly || screeningLoading) return;
    if (!screeningResult || !Array.isArray(screeningResult?.subjects)) return;
    if (!pepContextAssessmentSignature) return;
    if (pepContextRefreshRef.current === pepContextAssessmentSignature) return;

    let cancelled = false;

    async function refreshPepContextOnly() {
      const subjectByKey = new Map(
        (pepSubjects || []).map((subject) => [clean(subject?.key), subject])
      );

      const updatedSubjects = await asyncPool(
        3,
        screeningResult.subjects,
        async (subjectResult) => {
          const subject =
            subjectByKey.get(clean(subjectResult?.key)) || subjectResult || {};
          const matches = Array.isArray(subjectResult?.response?.results)
            ? subjectResult.response.results
            : [];
          const results = await asyncPool(4, matches, async (match) => ({
            ...match,
            contextAssessment: await fetchPepContextAssessment({
              subject,
              match,
            }),
          }));

          return {
            ...subjectResult,
            meta: subject?.meta || subjectResult?.meta || {},
            response: {
              ...(subjectResult?.response || {}),
              results,
            },
          };
        }
      );

      if (cancelled) return;

      const updatedResult = {
        ...screeningResult,
        subjects: updatedSubjects,
      };

      const selectedArr = Array.from(selectedKeys || []);
      const selectedMatches = [];
      for (const subjectResult of updatedSubjects) {
        const subjectKey = clean(subjectResult?.key) || "subject";
        const results = Array.isArray(subjectResult?.response?.results)
          ? subjectResult.response.results
          : [];
        for (const match of results) {
          const id = clean(match?.id) || "";
          const pepName = clean(match?.caption) || clean(match?.name) || "Unknown";
          const key = `${subjectKey}|${id ? `OS:${id}` : `OS:${pepName.toLowerCase()}`}`;
          if (!selectedArr.includes(key)) continue;
          const score = Number(match?.score);
          selectedMatches.push({
            key,
            id,
            matchedName: pepName,
            score: Number.isFinite(score) ? score : null,
            strength: strengthFromScore(score),
            classification: pepHitClassification(match),
            match: match?.match || null,
            record: match,
            contextAssessment:
              match?.contextAssessment || defaultPepContextAssessment(),
            screenedName: clean(subjectResult?.name || ""),
            screenedRoles: Array.isArray(subjectResult?.roles)
              ? subjectResult.roles
              : [],
            screenedMeta: subjectResult?.meta || {},
          });
        }
      }

      pepContextRefreshRef.current = pepContextAssessmentSignature;
      setScreeningResult(updatedResult);
      persistScreeningState({
        result: updatedResult,
        selectedKeys: selectedArr,
        selectedMatches,
        selectionSaved: !!form?.internalUse?.pepScreening?.selectionSaved,
        reviewStatus:
          form?.internalUse?.pepScreening?.reviewStatus || reviewStatus || null,
      });
    }

    refreshPepContextOnly().catch(() => {
      pepContextRefreshRef.current = "";
    });

    return () => {
      cancelled = true;
    };
  }, [
    form?.internalUse?.pepScreening,
    pepContextAssessmentSignature,
    pepSubjects,
    readOnly,
    reviewStatus,
    screeningLoading,
    screeningResult,
    selectedKeys,
  ]);

  const isReviewCompleted = reviewStatus === "completed";
  const isReviewDraft = reviewStatus === "draft";

  // ---------------- RESET SCREENING + COMPLIANCE IF SUBJECTS CHANGE ----------------
  const prevSigRef = useRef(subjectSignature);
  useEffect(() => {
    const prev = prevSigRef.current;
    if (prev !== subjectSignature) {
      setScreeningErr("");
      setScreeningResult(null);
      setPepWebAssessment([]);
      setPepWebAssessmentErr("");
      setPepWebAssessmentLoading(false);
      setSelectedOnly(false);
      setOpenKey(null);
      setSelectedKeys(new Set());
      setDraftKeys(new Set());
      setReviewStatus(null);

      if (typeof writeAt === "function") {
        writeAt(["internalUse", "pepScreening"], null);
        writeAt(["internalUse", "pepWebAssessment"], null);
      }

      resetComplianceAssessment();
      prevSigRef.current = subjectSignature;
    }
  }, [subjectSignature, writeAt]);

  // ---------------- RUN SCREENING ----------------
  async function runPepScreening() {
    setScreeningErr("");
    setScreeningResult(null);
    setPepWebAssessment([]);
    setPepWebAssessmentErr("");
    setPepWebAssessmentLoading(false);
    setOpenKey(null);
    setSelectedOnly(false);
    setSelectedKeys(new Set());
    setDraftKeys(new Set());
    setReviewStatus(null);

    if (typeof writeAt === "function") {
      writeAt(["internalUse", "pepScreening"], null);
      writeAt(["internalUse", "pepWebAssessment"], null);
    }

    resetComplianceAssessment();

    if (!pepSubjects || pepSubjects.length === 0) {
      setScreeningErr("Missing customer name.");
      return;
    }

    setScreeningLoading(true);

    try {
      const errors = [];
      const subjectResponses = [];
      for (const subj of pepSubjects) {
        try {
          const response = await fetchPepForName(subj.name, {
            limit: 12,
            threshold: 0.7,
          });
          const matches = Array.isArray(response?.results)
            ? response.results
            : [];
          const resultsWithContext = await asyncPool(4, matches, async (match) => ({
            ...match,
            contextAssessment: await fetchPepContextAssessment({
              subject: subj,
              match,
            }),
          }));
          subjectResponses.push({
            key: subj.key,
            name: subj.name,
            roles: subj.roles || [],
            meta: subj.meta || {},
            response: {
              ...response,
              results: resultsWithContext,
            },
          });
        } catch (e) {
          errors.push({
            key: subj.key,
            name: subj.name,
            message: e?.message || "Screening failed",
          });
        }
      }

      await trackScreeningUsage({
        form,
        screeningType: "pep",
        namesCount: pepSubjects.length,
        subjects: pepSubjects,
      });

      const combined = { subjects: subjectResponses, errors };

      setScreeningResult(combined);

      persistScreeningState({
        result: combined,
        selectedKeys: [],
        selectedMatches: [],
        selectionSaved: false,
        reviewStatus: null,
      });

      const totalMatches = subjectResponses.reduce((sum, s) => {
        const n = Array.isArray(s?.response?.results)
          ? s.response.results.length
          : 0;
        return sum + n;
      }, 0);

      if (totalMatches === 0 && typeof writeAt === "function") {
        writeAt(["pep", "staffDecision"], "no_exposure");
        setReviewStatus("completed");
        persistScreeningState({
          result: combined,
          selectedKeys: [],
          selectedMatches: [],
          selectionSaved: true,
          reviewStatus: "completed",
        });
        setSelectedOnly(true);
        scrollPepAnchorThenCollapse();
      }
    } catch (e) {
      setScreeningErr(e?.message || "Screening failed.");
    } finally {
      setScreeningLoading(false);
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      runScreening: runPepScreening,
      hasRun: hasResult,
      isLoading: screeningLoading,
      canRun: !readOnly && !screeningLoading && !!pepSubjects?.length,
    }),
    [hasResult, screeningLoading, readOnly, pepSubjects]
  );

  function clearScreening() {
    setScreeningErr("");
    setScreeningResult(null);
    setPepWebAssessment([]);
    setPepWebAssessmentErr("");
    setPepWebAssessmentLoading(false);
    setSelectedOnly(false);
    setOpenKey(null);
    setSelectedKeys(new Set());
    setDraftKeys(new Set());
    setReviewStatus(null);

    if (typeof writeAt === "function") {
      writeAt(["internalUse", "pepScreening"], null);
      writeAt(["internalUse", "pepWebAssessment"], null);
    }

    resetComplianceAssessment();
  }

  // ---------------- MATCH NORMALIZATION (PER SUBJECT) ----------------
  const uniqueMatches = useMemo(() => {
    const subjects = Array.isArray(screeningResult?.subjects)
      ? screeningResult.subjects
      : [];
    if (!subjects.length) return [];

    const out = [];

    for (const subj of subjects) {
      const subjKey = clean(subj?.key);
      const subjName = clean(subj?.name);
      const subjRoles = Array.isArray(subj?.roles) ? subj.roles : [];
      const subjMeta = subj?.meta || {};

      const results = Array.isArray(subj?.response?.results)
        ? subj.response.results
        : [];
      for (const m of results) {
        const id = clean(m?.id) || "";
        const pepName = clean(m?.caption) || clean(m?.name) || "Unknown";
        const key = `${subjKey || "subject"}|${
          id ? `OS:${id}` : `OS:${pepName.toLowerCase()}`
        }`;

        const score = Number(m?.score);
        const strength = strengthFromScore(score);
        const classification = pepHitClassification(m);

        out.push({
          _key: key,
          id,
          name: pepName,
          score: Number.isFinite(score) ? score : null,
          strength,
          classification,
          matchType: m?.match || null,
          record: m,
          contextAssessment: m?.contextAssessment || defaultPepContextAssessment(),
          subject: {
            key: subjKey,
            name: subjName,
            roles: subjRoles,
            meta: subjMeta,
          },
        });
      }
    }

    const rank = (s) => (s === "strong" ? 0 : s === "possible" ? 1 : 2);

    out.sort((a, b) => {
      const aSel = draftKeys.has(a._key) ? 0 : 1;
      const bSel = draftKeys.has(b._key) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return (
        rank(a.strength) - rank(b.strength) || a.name.localeCompare(b.name)
      );
    });

    return out;
  }, [screeningResult, draftKeys]);

  const styles = useMemo(
    () =>
      toneStyles({
        hasResult: !!screeningResult,
        hasMatches: uniqueMatches.length > 0,
      }),
    [screeningResult, uniqueMatches.length]
  );

  const visibleMatches = useMemo(() => {
    if (!screeningResult) return [];
    if (!selectedOnly) return uniqueMatches;
    return uniqueMatches.filter((m) => selectedKeys.has(m._key));
  }, [screeningResult, selectedOnly, uniqueMatches, selectedKeys]);

  const pepReviewHeaderSticky = !selectedOnly;
  const pepReviewHeaderGlass = pepReviewHeaderSticky && pepReviewScrolled;

  const groupedPepReviewItems = useMemo(() => {
    const map = new Map();
    const subjectOrder = new Map();
    (pepSubjects || []).forEach((subject, index) => {
      const key = clean(subject?.key) || `subject_${index}`;
      subjectOrder.set(key, index);
    });

    function ensureGroup({ key, name, roles, meta }) {
      const groupKey = clean(key) || `subject:${normKey(name || "subject")}`;
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          name: clean(name) || "Subject",
          roles: Array.isArray(roles) ? roles : [],
          meta: meta || {},
          matches: [],
          aiItem: null,
          order: subjectOrder.has(groupKey)
            ? subjectOrder.get(groupKey)
            : Number.MAX_SAFE_INTEGER,
        });
      }
      const group = map.get(groupKey);
      if (!group.name && name) group.name = clean(name);
      if (!group.roles.length && Array.isArray(roles)) group.roles = roles;
      if (!Object.keys(group.meta || {}).length && meta) group.meta = meta;
      return group;
    }

    (visibleMatches || []).forEach((match) => {
      const subject = match?.subject || {};
      const group = ensureGroup({
        key: subject.key,
        name: subject.name,
        roles: subject.roles,
        meta: subject.meta,
      });
      group.matches.push(match);
    });

    (visiblePepWebAssessment || []).forEach((item, index) => {
      const group = ensureGroup({
        key: item?.subjectKey || `ai_${index}`,
        name: item?.subjectName,
        roles: item?.roles,
        meta: {},
      });
      group.aiItem = item;
    });

    if (
      (pepWebAssessmentLoading || pepWebAssessmentErr) &&
      !(visiblePepWebAssessment || []).length
    ) {
      (pepWebAssessmentSubjects || []).forEach((subject, index) => {
        ensureGroup({
          key: subject?.key || `assessment_${index}`,
          name: subject?.name,
          roles: subject?.roles,
          meta: subject?.meta,
        });
      });
    }

    const groups = Array.from(map.values());
    const visibleGroups = isReviewCompleted
      ? groups.filter((group) => group.matches.length > 0)
      : groups;

    return visibleGroups.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.name.localeCompare(b.name);
    });
  }, [
    isReviewCompleted,
    pepSubjects,
    visibleMatches,
    visiblePepWebAssessment,
    pepWebAssessmentLoading,
    pepWebAssessmentErr,
    pepWebAssessmentSubjects,
  ]);

  function renderDetails(rec) {
    const r = rec || {};
    const props = r?.properties || r?.entity?.properties || null;
    return renderPepDetailsFromProperties(props);
  }

  function toggleDraftSelected(key) {
    setDraftKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function buildSelectedMatches(selectedArr) {
    return uniqueMatches
      .filter((m) => selectedArr.includes(m._key))
      .map((m) => ({
        key: m._key,
        id: m.id,
        matchedName: m.name,
        score: m.score,
        strength: m.strength,
        classification: m.classification,
        match: m.matchType,
        record: m.record,
        contextAssessment:
          m.contextAssessment || defaultPepContextAssessment(),
        screenedName: m?.subject?.name || "",
        screenedRoles: m?.subject?.roles || [],
        screenedMeta: m?.subject?.meta || {},
      }));
  }

  function saveSelectionDraft() {
    const selectedArr = Array.from(draftKeys);
    const selectedMatches = buildSelectedMatches(selectedArr);

    persistScreeningState({
      result: screeningResult || null,
      selectedKeys: selectedArr,
      selectedMatches,
      selectionSaved: true,
      reviewStatus: "draft",
    });

    setSelectedKeys(new Set(selectedArr));
    setSelectedOnly(true);
    setOpenKey(null);
    setReviewStatus("draft");
  }

  function saveSelectionCompleted() {
    const selectedArr = Array.from(draftKeys);
    const selectedMatches = buildSelectedMatches(selectedArr);

    persistScreeningState({
      result: screeningResult || null,
      selectedKeys: selectedArr,
      selectedMatches,
      selectionSaved: true,
      reviewStatus: "completed",
    });

    setSelectedKeys(new Set(selectedArr));
    setSelectedOnly(true);
    setOpenKey(null);
    setReviewStatus("completed");

    if (selectedArr.length === 0) {
      setStaffDecision("no_exposure");
      scrollPepAnchorThenCollapse();
    } else {
      setStaffDecision("flagged");
      if (typeof writeAt === "function")
        writeAt(["pep", "staffDecision"], "flagged");
      if (actionRequiredBadgeRevealTimerRef.current) {
        window.clearTimeout(actionRequiredBadgeRevealTimerRef.current);
      }
      setActionRequiredBadgeVisible(false);
      setComplianceAssessmentIntroBackgroundActive(true);
      setComplianceAssessmentGlowActive(true);
      actionRequiredBadgeRevealTimerRef.current = window.setTimeout(() => {
        setActionRequiredBadgeVisible(true);
        actionRequiredBadgeRevealTimerRef.current = null;
      }, 720);
    }
  }

  function editSelection() {
    setDraftKeys(new Set(selectedKeys));
    setSelectedOnly(false);
    setOpenKey(null);
    setReviewStatus(null);

    persistScreeningState({
      result: screeningResult || null,
      selectedKeys: Array.from(selectedKeys),
      selectedMatches: form?.internalUse?.pepScreening?.selectedMatches || [],
      selectionSaved: false,
      reviewStatus: null,
    });

    resetComplianceAssessment();
  }

  // ---------------- STAFF DECISION ----------------
  const staffDecision = clean(pep?.staffDecision) || "";
  function setStaffDecision(dec) {
    if (typeof writeAt !== "function") return;
    writeAt(["pep", "staffDecision"], dec);
  }

  const phase1 =
    staffDecision === "no_exposure"
      ? "no_exposure"
      : staffDecision
      ? "flagged"
      : "";
  const phase2 =
    staffDecision === "flagged_remediated"
      ? "flagged_remediated"
      : staffDecision === "flagged_confirmed"
      ? "flagged_confirmed"
      : "";

  function setPhase1(v) {
    if (!hasResult) return;
    if (!isReviewCompleted) return;
    if (v === "no_exposure") setStaffDecision("no_exposure");
    else setStaffDecision("flagged");
  }
  function setPhase2(v) {
    if (!hasResult) return;
    if (!isReviewCompleted) return;
    if (v === "flagged_remediated" || v === "flagged_confirmed") {
      if (staffDecision === v) {
        setStaffDecision("flagged");
        return;
      }
      setStaffDecision(v);
      scrollPepAnchorThenCollapse();
    }
  }

  // ---------------- CLIENT DECLARATION ----------------
  const clientDeclared = !!pep?.clientDeclared;

  const potentialCount = hasResult ? uniqueMatches.length : 0;
  const savedCount = selectedKeys?.size || 0;
  const draftCount = draftKeys?.size || 0;

  const complianceUsable = !hasResult
    ? false
    : potentialCount === 0
    ? true
    : isReviewCompleted;
  const shouldHighlightComplianceAssessment =
    isReviewCompleted && selectedKeys?.size > 0 && phase1 === "flagged" && !phase2;

  const subjectCounts = useMemo(() => {
    const rolesCount = {
      Client: 0,
      "Direct shareholder": 0,
      UBO: 0,
      "Authorised signatory": 0,
    };
    (pepSubjects || []).forEach((s) => {
      const roles = (Array.isArray(s?.roles) ? s.roles : [])
        .map((r) => clean(r))
        .filter(Boolean);
      const lower = roles.map((r) => r.toLowerCase());
      const normalizedRole = lower.some((r) => r.includes("client"))
        ? "Client"
        : lower.some((r) => r.includes("direct shareholder"))
        ? "Direct shareholder"
        : lower.some(
            (r) =>
              r === "ubo" ||
              r.includes("ubo") ||
              r === "settlor" ||
              r === "trustee" ||
              r === "beneficiary"
          )
        ? "UBO"
        : lower.some((r) => r.includes("authorised signatory"))
        ? "Authorised signatory"
        : "";
      if (!normalizedRole) return;
      rolesCount[normalizedRole] += 1;
    });
    return rolesCount;
  }, [pepSubjects]);

  function renderPepMatchRow(m, idx = 0) {
    const confirmed = selectedOnly
      ? selectedKeys.has(m._key)
      : draftKeys.has(m._key);
    const expanded = openKey === m._key;
    const niceReason = labelFromMatchType(m.matchType);
    const showReason = niceReason && niceReason !== "Potential match";
    const context = m.contextAssessment || defaultPepContextAssessment();
    const snap = summarizePepSnapshotFromRecord(m.record);
    const parts = [];
    if (snap.birthDate) parts.push(`Birth: ${snap.birthDate}`);
    if (snap.position) parts.push(`Position: ${snap.position}`);
    if (snap.notes) parts.push(`Notes: ${clampText(snap.notes, 140)}`);

    return (
      <motion.div
        key={m._key}
        className="list-group-item"
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            ...T.fast,
            delay: Math.min(idx, 8) * 0.015,
          },
        }}
        exit={{ opacity: 0, y: 4, transition: T.fast }}
        style={{
          borderLeft: confirmed ? "4px solid #EBE9E8" : "1px solid #DEE2E6",
          background: confirmed ? "#FFFDFC" : "#FBFBFB",
          color: "#837E7B",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between gap-3"
          style={{ cursor: "pointer" }}
          onClick={() => setOpenKey(expanded ? null : m._key)}
          role="button"
        >
          <div style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold" style={{ color: "#2f3441" }}>
                {m.name}
              </div>

              {pepHitClassificationBadge(m.classification)}
              {overallPepMatchBadge(m.strength, context)}

              {confirmed ? badge("Match confirmed", "orange") : null}
            </div>

            <div className="mt-1">
              {showReason ? (
                <div className="small text-muted mt-1">{niceReason}</div>
              ) : null}

              {parts.length ? (
                <div
                  className="small text-muted mt-1"
                  style={{
                    display: "-webkit-box",
                    opacity: 0.9,
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 6,
                  }}
                >
                  {parts.join(" · ")}
                </div>
              ) : null}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            {!selectedOnly ? (
              <>
                <button
                  type="button"
                  className="badge btn-lightBlue-outline"
                  style={{
                    fontWeight: 400,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 26,
                    padding: "0 10px",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDraftSelected(m._key);
                  }}
                  title={confirmed ? "Undo confirmation" : "Confirm this match"}
                >
                  {confirmed ? "Unconfirm" : "Confirm match"}
                </button>

                {confirmed ? (
                  <i
                    className="bi bi-bookmark-check-fill"
                    style={{ color: "#798CA0" }}
                  />
                ) : (
                  <i
                    className="bi bi-bookmark-plus"
                    style={{ color: "#798CA0" }}
                  />
                )}
              </>
            ) : (
              <span />
            )}

            <span className="text-muted" title="Show details">
              <i className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
            </span>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.div key="details" {...detailsIn} style={{ overflow: "hidden" }}>
              <div className="mt-2 p-2 rounded-3 small" style={{ background: "#F6F4F2" }}>
                <div
                  className="d-flex align-items-center gap-2 flex-wrap"
                  style={{ color: "#2f3441" }}
                >
                  <span style={{ marginRight: 4 }}>
                    {pepContextAssessmentLine(context)}
                  </span>
                  <PepContextCheckChip
                    label="Name"
                    value={`${clean(m.strength)} match`}
                  />
                  <PepContextCheckChip
                    label="Nationality"
                    value={context.checks?.nationality}
                  />
                  <PepContextCheckChip
                    label="Domicile"
                    value={context.checks?.domicile}
                  />
                  <PepContextCheckChip
                    label="Age"
                    value={context.checks?.age}
                  />
                </div>
              </div>
              <div className="mt-2 p-2 rounded-3" style={{ background: "#F6F4F2" }}>
                {renderDetails(m.record)}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead position-relative">
        <h5 className="kycSectionTitle mb-0">{sectionNo} | PEP Exposure</h5>
      </div>

      <div className="kycSectionBody">
        {screeningErr ? (
          <div className="alert alert-danger mb-3">{screeningErr}</div>
        ) : null}

        {/* ----------- TOP SUMMARY CARD ----------- */}

        {/* background color top card overview main */}
        <div
          className="p-3 border rounded-3 mb-3"
          // style={{ background: "#C88294" }}
          style={{ background: "#EBECEF", cursor: readOnly ? "default" : "pointer" }}
          role={readOnly ? undefined : "button"}
          tabIndex={readOnly ? undefined : 0}
          onClick={readOnly ? undefined : toggleDetails}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleDetails();
                  }
                }
          }
        >
          {(() => {
            const clientStatusBadge = clientDeclared
              ? badge("Declared", "warning")
              : badge("Not declared", "success");
            const clientHint = clientDeclared
              ? "Client reports exposure"
              : "Client reports no exposure";

            const screeningStatusBadge = !hasResult
              ? badge("Pending", "grey")
              : screeningLoading
              ? badge("Running", "warning")
              : potentialCount === 0
              ? badge("No matches found", "success")
              : isReviewCompleted
              ? badge(
                  savedCount > 0
                    ? `${savedCount} confirmed`
                    : "All hits cleared",
                  savedCount > 0 ? "danger" : "success"
                )
              : badge("Pending", "grey");

            const screeningHint = !hasResult ? (
              "Screening not started"
            ) : potentialCount === 0 ? (
              "No potential matches found"
            ) : isReviewCompleted ? (
              <div className="d-flex flex-column gap-1">
                <div>
                  {potentialCount} potential ·{" "}
                  {savedCount > 0 ? (
                    <span>{savedCount} confirmed</span>
                  ) : (
                    <span>0 confirmed</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column gap-1">
                <div className="text-muted">Review screening hits</div>
              </div>
            );

            const complianceStatusBadge = !hasResult
              ? badge("Pending", "grey")
              : !isReviewCompleted && potentialCount > 0
              ? badge("Pending", "grey")
              : !staffDecision
              ? potentialCount === 0
                ? badge("PEP cleared", "success")
                : badge("Pending", "grey")
              : staffDecision === "no_exposure"
              ? badge("PEP cleared", "success")
              : staffDecision === "flagged"
              ? badge("PEP identified", "warning")
              : staffDecision === "flagged_remediated"
              ? badge("PEP cleared", "success")
              : staffDecision === "flagged_confirmed"
              ? badge("PEP confirmed", "danger")
              : badge("Pending", "grey");

            const complianceHint = !hasResult
              ? "Set outcome after screening"
              : !isReviewCompleted && potentialCount > 0
              ? "Complete the hit review first"
              : !staffDecision
              ? potentialCount === 0
                ? "No PEP exposure identified"
                : "Set outcome after review"
              : staffDecision === "no_exposure"
              ? "No PEP exposure identified"
              : staffDecision === "flagged"
              ? "PEP identified · Pending decision"
              : staffDecision === "flagged_remediated"
              ? "Exposure flagged · Remediated"
              : staffDecision === "flagged_confirmed"
              ? "Confirmed PEP identification"
              : "Set outcome after review";

            const overall = (() => {
              if (!hasResult || screeningLoading) {
                return {
                  term: "Pending",
                  bg: "#F4F5F7",
                  border: "#D9DCE1",
                  icon: "bi-hourglass",
                  color: "#6B7C5F",
                  lottie: overallPendingAnim,
                };
              }
              if (staffDecision === "flagged_confirmed") {
                return {
                  term: "PEP confirmed",
                  bg: "#FDEBEC",
                  border: "#F3B6BC",
                  icon: "bi-exclamation-circle-fill",
                  color: "#C88294",
                  lottie: overallAlertAnim,
                };
              }
              if (
                staffDecision === "no_exposure" ||
                staffDecision === "flagged_remediated"
              ) {
                return {
                  term: "PEP cleared",
                  bg: "#EAF7EE",
                  border: "#BFE6CC",
                  icon: "bi-check-circle",
                  color: "#6B7C5F",
                  lottie: overallClearedAnim,
                };
              }
              if (potentialCount === 0) {
                return {
                  term: "PEP cleared",
                  bg: "#EAF7EE",
                  border: "#BFE6CC",
                  icon: "bi-check-circle",
                  color: "#6B7C5F",
                  lottie: overallClearedAnim,
                };
              }
              return {
                term: "Pending",
                bg: "#F4F5F7",
                border: "#FFD79A",
                icon: "bi-hourglass",
                color: "#6B7C5F",
                lottie: overallPendingAnim,
              };
            })();

            return (
              <>
                <div
                  className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                  style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                >
                  <div>
                    <div
                      className="fw-semibold d-flex align-items-center gap-2"
                      style={{ color: "#2f3441" }}
                    >
                      {/* <i
                        className="bi bi-bank2 me-2"
                        style={{ color: "#A1A5AE", fontSize: 25 }}
                      /> */}
                      <span
                        style={{
                          width: 40,
                          height: 40,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        <Lottie
                          animationData={pepAnim}
                          loop
                          autoplay
                          style={{ width: 40, height: 40 }}
                        />
                      </span>
                      PEP overview
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDetails();
                    }}
                    disabled={readOnly}
                    style={{ height: 30 }}
                  >
                    {showDetails ? (
                      <>
                        <i className="bi bi-chevron-up me-1" />
                        Hide details
                      </>
                    ) : (
                      <>
                        <i className="bi bi-chevron-down me-1" />
                        Show details
                      </>
                    )}
                  </button>
                </div>
                {/* <div
                  className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                  style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                >
                  <div>
                    <div
                      className="fw-semibold d-flex align-items-center gap-2"
                      style={{ color: "#2f3441" }}
                    >
                      <i
                        className="bi bi-bank2 me-2"
                        style={{ color: "#A1A5AE", fontSize: 25 }}
                      />
                      PEP overview
                    </div>
                  </div>
                </div> */}

                <hr className="my-3" />

                <div className="row g-2">
                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-person-check" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              id="fi_8703172"
                              height="512"
                              viewBox="0 0 60 60"
                              width="512"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill="currentColor"
                                d="m34.82 8.06c-5.71 0-10.35 4.64-10.35 10.35s4.64 10.35 10.35 10.35 10.35-4.64 10.35-10.35-4.65-10.35-10.35-10.35zm0 18.69c-4.6 0-8.35-3.74-8.35-8.35s3.74-8.35 8.35-8.35c4.6 0 8.35 3.74 8.35 8.35s-3.75 8.35-8.35 8.35z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="m22.54 38.64c.66.61 1.04 1.49 1.04 2.41v9.3c0 .55.45 1 1 1s1-.45 1-1v-9.3c0-1.47-.61-2.88-1.68-3.88-5.42-5.04-10.5-12.12-15.54-21.67-.73-1.39-.32-3.14.95-4.07.75-.54 1.7-.73 2.6-.52s1.66.8 2.09 1.61c3.05 5.79 6.49 11.08 10.21 15.73 1.83 2.28 4.56 3.58 7.49 3.58h9.16c6.14 0 11.14 5 11.14 11.14v7.37c0 .55.45 1 1 1s1-.45 1-1v-7.37c0-7.25-5.89-13.14-13.14-13.14h-9.16c-2.33 0-4.49-1.03-5.93-2.83-3.64-4.54-7.01-9.73-10-15.41-.7-1.33-1.94-2.29-3.4-2.63s-3-.03-4.23.85c-2.08 1.51-2.74 4.35-1.55 6.62 5.15 9.76 10.36 17.02 15.95 22.21z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="m46 43.71v7.23c0 .55.45 1 1 1s1-.45 1-1v-7.23c0-.55-.45-1-1-1s-1 .44-1 1z"
                              ></path>
                            </svg>
                          </span>
                          <div className="fw-semibold small">
                            Client declaration
                          </div>
                        </div>
                        {clientStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">{clientHint}</div>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-search" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              height="512"
                              viewBox="0 0 64 64"
                              width="512"
                              xmlns="http://www.w3.org/2000/svg"
                              id="fi_6772342"
                            >
                              <g
                                id="_10-Security_Gate"
                                data-name="10-Security Gate"
                              >
                                <path
                                  fill="currentColor"
                                  d="m53 2h-42a.99974.99974 0 0 0 -1 1v58a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-43h32v43a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-58a.99974.99974 0 0 0 -1-1zm-39 58h-2v-56h2zm34-44h-32v-12h32zm4 44h-2v-56h2zm-26-46h12a.99974.99974 0 0 0 1-1v-6a.99974.99974 0 0 0 -1-1h-12a.99974.99974 0 0 0 -1 1v6a.99974.99974 0 0 0 1 1zm1-6h10v4h-10zm-9 0a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm23-4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm-4 20h-.54047a6 6 0 1 0 -8.91906 0h-.54047a7.00818 7.00818 0 0 0 -7 7v6a3.00328 3.00328 0 0 0 3 3h2v11a3.00328 3.00328 0 0 0 3 3h8a3.00328 3.00328 0 0 0 3-3v-11h2a3.00328 3.00328 0 0 0 3-3v-6a7.00818 7.00818 0 0 0 -7-7zm-5-8a4 4 0 1 1 -4 4 4.00427 4.00427 0 0 1 4-4zm10 21a1.001 1.001 0 0 1 -1 1h-2v-5a1 1 0 0 0 -2 0v18a1.001 1.001 0 0 1 -1 1h-3v-9a1 1 0 0 0 -2 0v9h-3a1.001 1.001 0 0 1 -1-1v-18a1 1 0 0 0 -2 0v5h-2a1.001 1.001 0 0 1 -1-1v-6a5.00588 5.00588 0 0 1 5-5h10a5.00588 5.00588 0 0 1 5 5zm-34-13a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-4.55469-9.168a1 1 0 1 1 1.10938-1.664l3 2a1 1 0 1 1 -1.10938 1.664zm4.38669 16.61331a.9993.9993 0 0 1 -.27731 1.38669l-3 2a1 1 0 0 1 -1.10938-1.664l3-2a.99886.99886 0 0 1 1.38669.27731zm54.168-7.44531a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-5.832-7.44531a.9993.9993 0 0 1 .27734-1.38672l3-2a1 1 0 1 1 1.10935 1.66403l-3 2a.99992.99992 0 0 1 -1.38672-.27734zm4.38669 16.61331a1 1 0 1 1 -1.10938 1.664l-3-2a1 1 0 0 1 1.10938-1.664z"
                                ></path>
                              </g>
                            </svg>
                          </span>
                          <div className="fw-semibold small">Screening</div>
                        </div>
                        {screeningStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">
                        {screeningHint}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-clipboard-check" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              id="fi_14979987"
                              viewBox="0 0 60 60"
                            >
                              <path
                                fill="currentColor"
                                d="M36.949,31.846A3.005,3.005,0,0,0,39,29V26a9,9,0,0,0-18,0v3a3.005,3.005,0,0,0,2.051,2.846A7,7,0,0,0,26,36.734v1.5l-3.9,1.053a7.242,7.242,0,0,0-5.228,4.542L15.6,47.4l-2.4-1.917a1.579,1.579,0,0,0-.2-.13V42.68a2.685,2.685,0,0,0-5.29-.642l-.007.026a4.629,4.629,0,0,0-4.211,1.453,1.892,1.892,0,0,0,.617,2.995,19.054,19.054,0,0,1,3.045,1.7c.465.336.843.633,1.214.935a1.83,1.83,0,0,0,.651,1.2l6.43,4.77a3.45,3.45,0,0,0,3.086.8,3.406,3.406,0,0,0,2.415-1.958L22,51.992V58a2,2,0,0,0,2,2H36a2,2,0,0,0,2-2V51.987l1.013,1.892a3.456,3.456,0,0,0,2.452,2.036,3.4,3.4,0,0,0,3.016-.744l6.568-4.882a1.769,1.769,0,0,0,.583-1.14c.371-.3.747-.6,1.207-.933a19.142,19.142,0,0,1,3.055-1.706,1.891,1.891,0,0,0,.6-3,4.641,4.641,0,0,0-4.2-1.442l-.007-.029A2.685,2.685,0,0,0,47,42.68v2.668a1.718,1.718,0,0,0-.2.131l-2.326,1.937-1.345-3.588a7.219,7.219,0,0,0-5.184-4.53L34,38.234v-1.5A7,7,0,0,0,36.949,31.846ZM30,19a7.009,7.009,0,0,1,7,7v.179c-.037-.013-.072-.032-.11-.044a3.073,3.073,0,0,0-4.626-1.743,4.521,4.521,0,0,1-4.527,0,3.075,3.075,0,0,0-4.629,1.743c-.037.011-.071.03-.108.043V26A7.009,7.009,0,0,1,30,19ZM24,30a1,1,0,0,1,0-2,1.081,1.081,0,0,0,1-1.075,1.031,1.031,0,0,1,.564-.9,1.1,1.1,0,0,1,.51-.129,1.023,1.023,0,0,1,.564.169,6.5,6.5,0,0,0,6.723,0,1.041,1.041,0,0,1,1.073-.04A1.064,1.064,0,0,1,35,27a1,1,0,0,0,1,1,1,1,0,0,1,0,2,1,1,0,0,0-1,1,5,5,0,0,1-10,0A1,1,0,0,0,24,30Zm9.439,10.155,1.921.517a6.768,6.768,0,0,1-2.036,2.614.392.392,0,0,1-.365.049.38.38,0,0,1-.251-.261,5.694,5.694,0,0,0-.793-1.654A4.831,4.831,0,0,0,33.439,40.155ZM31.984,52.646,30,53.965l-1.984-1.319L28.862,47h2.276ZM28.721,45l-.116-.348a2.294,2.294,0,0,0,.6-1.006A3.239,3.239,0,0,1,30,42.215a3.2,3.2,0,0,1,.788,1.417,2.323,2.323,0,0,0,.608,1.019L31.279,45Zm-1.433-1.911a.373.373,0,0,1-.247.246.4.4,0,0,1-.37-.052,6.8,6.8,0,0,1-2.034-2.609l1.924-.519a4.851,4.851,0,0,0,1.525,1.266A5.738,5.738,0,0,0,27.288,43.089ZM5.075,44.754a2.634,2.634,0,0,1,2.935-.522,1,1,0,0,0,1.38-.669l.259-1.037A.7.7,0,0,1,10.32,42a.681.681,0,0,1,.68.68v2.769a1.8,1.8,0,0,0-.233.205l-1.524,1.63c-.28-.221-.581-.451-.924-.7A20.843,20.843,0,0,0,5.075,44.754ZM49.68,42a.7.7,0,0,1,.671.522l.259,1.041a1,1,0,0,0,1.38.669,2.69,2.69,0,0,1,2.962.509,20.887,20.887,0,0,0-3.278,1.849c-.339.245-.637.473-.916.693l-1.525-1.629A1.8,1.8,0,0,0,49,45.449V42.68A.681.681,0,0,1,49.68,42Zm-8.424,2.53L42.6,48.118a1.956,1.956,0,0,0,3.052.84l2.248-1.8,1.647,1.759-6.327,4.7a1.478,1.478,0,0,1-1.321.342,1.542,1.542,0,0,1-1.087-.949l-2.931-5.486A1,1,0,0,0,36,48V58H24V48a1,1,0,0,0-1.882-.472l-2.969,5.564a1.481,1.481,0,0,1-1.049.871,1.532,1.532,0,0,1-1.389-.4L10.45,48.921,12.1,47.159l2.248,1.8a1.955,1.955,0,0,0,3.052-.84l1.345-3.586a5.271,5.271,0,0,1,3.832-3.3l.1-.027a9.027,9.027,0,0,0,2.805,3.692,2.356,2.356,0,0,0,1.241.443l.25.75-.956,6.388a1.828,1.828,0,0,0,.794,1.774L28.989,55.7a1.823,1.823,0,0,0,2.022,0l2.176-1.446a1.831,1.831,0,0,0,.793-1.781l-.955-6.381.247-.742a2.359,2.359,0,0,0,1.239-.449,8.969,8.969,0,0,0,2.806-3.7l.151.04A5.247,5.247,0,0,1,41.256,44.53ZM32,38.743a2.944,2.944,0,0,1-2,1.243,2.946,2.946,0,0,1-2-1.241V37.706a6.95,6.95,0,0,0,4,0Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M11,22A11,11,0,1,0,0,11,11.013,11.013,0,0,0,11,22ZM11,2a9,9,0,1,1-9,9A9.01,9.01,0,0,1,11,2Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M49,0A11,11,0,1,0,60,11,11.013,11.013,0,0,0,49,0Zm0,20a9,9,0,1,1,9-9A9.01,9.01,0,0,1,49,20Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M7.293,14.707a1,1,0,0,0,1.414,0L11,12.414l2.293,2.293a1,1,0,0,0,1.414-1.414L12.414,11l2.293-2.293a1,1,0,1,0-1.414-1.414L11,9.586,8.707,7.293A1,1,0,1,0,7.293,8.707L9.586,11,7.293,13.293A1,1,0,0,0,7.293,14.707Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M51.168,7.445l-3.323,4.986-1.138-1.138a1,1,0,0,0-1.414,1.414l2,2A1,1,0,0,0,48,15c.032,0,.065,0,.1-.005a1,1,0,0,0,.733-.44l4-6a1,1,0,0,0-1.664-1.11Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M50,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M12,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                              ></path>
                            </svg>
                          </span>
                          <div className="fw-semibold small">Assessment</div>
                        </div>
                        {complianceStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">
                        {complianceHint}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-2">
                    <div
                      className="p-2 rounded-3 border h-100 d-flex align-items-center justify-content-center text-center"
                      style={{
                        background: overall.bg,
                        borderColor: overall.border,
                      }}
                    >
                      <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                        <span
                          style={{
                            width: 35,
                            height: 35,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            lineHeight: 1,
                          }}
                          aria-hidden="true"
                        >
                          <Lottie
                            animationData={overall.lottie}
                            loop
                            autoplay
                            style={{ width: 35, height: 35 }}
                          />
                        </span>
                        {/* <i
                          className={`bi ${overall.icon}`}
                          style={{
                            fontSize: 24,
                            lineHeight: 1,
                            color: overall.color,
                          }}
                          aria-hidden="true"
                        /> */}
                        <div
                          className="fw-semibold small"
                          style={{ color: overall.color, fontSize: 12 }}
                        >
                          {overall.term}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Extensions: card — arrow — card, rendered inside the overview band.
              Overflow is released after the height animation so the arrow can
              escape the band and land in the screening card below. */}
          <AnimatePresence initial={false}>
            {showDetails ? (
              <motion.div
                key="pep_ext_row"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={T.med}
                onAnimationStart={() => setExtRevealed(false)}
                onAnimationComplete={() => setExtRevealed(!!showDetails)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                style={{
                  cursor: "default",
                  overflow: extRevealed ? "visible" : "hidden",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <div className="row g-2" style={{ paddingTop: 8 }}>
                {/* 1) Client declaration — same idea as the middle: a short white
                    3D arrow drops from the tile above INTO this card (card itself flat) */}
                <div className="col-12 col-md d-flex">
                  <div className="w-100 position-relative" style={{ paddingTop: 42 }}>
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 80,
                        zIndex: 2,
                        WebkitMaskImage:
                          "linear-gradient(to bottom, transparent 0px, #000 26px)",
                        maskImage:
                          "linear-gradient(to bottom, transparent 0px, #000 26px)",
                      }}
                    >
                      <div className="w-100 position-relative" style={{ height: 60 }}>
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            top: 44,
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            border: "1.5px solid rgba(76, 84, 101, 0.16)",
                            color: "rgba(76, 84, 101, 0.26)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            fontWeight: 600,
                            pointerEvents: "none",
                          }}
                        >
                          1
                        </span>
                        <div
                          style={{
                            height: 22,
                            width: "90%",
                            margin: "0 auto -1px",
                            position: "relative",
                            zIndex: 1,
                            background: "#F4F5F7",
                            border: "1px solid #D9DCE1",
                            borderBottom: "none",
                            borderRadius: "10px 10px 0 0",
                          }}
                        />
                        <svg
                          width="100%"
                          height="44"
                          viewBox="0 0 400 44"
                          preserveAspectRatio="none"
                          style={{ display: "block", flex: "0 0 auto", margin: "0 auto" }}
                          aria-hidden="true"
                        >
                          <path
                            d="M16 0 H384 C398 0 401 4 394 8.5 L216 39 C205 43.8 195 43.8 184 39 L6 8.5 C-1 4 2 0 16 0 Z"
                            fill="#F4F5F7"
                            stroke="#D9DCE1"
                            strokeWidth={1}
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                      </div>
                    </div>

                  <div
                    className="border rounded-3 h-100 d-flex flex-column w-100"
                    style={{ padding: "40px 16px 16px", background: "#fff" }}
                  >
                    <div className="mt-0">
                      <div className="text-muted small mb-1">
                        PEP declared by client?
                      </div>

                      <div
                        className="btn-group"
                        role="group"
                        aria-label="Client declaration"
                        style={{ marginTop: 4 }}
                      >
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            clientDeclared
                              ? "btn-toggle-off-lightgrey"
                              : "btn-toggle-on-lightgrey"
                          }`}
                          disabled={readOnly}
                          onClick={() =>
                            writeAt?.(["pep", "clientDeclared"], false)
                          }
                        >
                          No
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            clientDeclared
                              ? "btn-toggle-on-lightgrey"
                              : "btn-toggle-off-lightgrey"
                          }`}
                          disabled={readOnly}
                          onClick={() =>
                            writeAt?.(["pep", "clientDeclared"], true)
                          }
                        >
                          Yes
                        </button>
                      </div>

                    </div>

                    <div className="mt-3">
                      <div
                        className="text-muted small"
                        style={{ marginTop: 5, marginBottom: 8 }}
                      >
                        Client comments
                      </div>
                      <textarea
                        ref={clientCommentsRef}
                        className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                          "pep",
                          "clientDetails",
                        ])}`}
                        rows={1}
                        disabled={readOnly}
                        value={pep?.clientDetails || ""}
                        onChange={(e) => {
                          const el = e.currentTarget;
                          writeAt?.(["pep", "clientDetails"], e.target.value);
                          window.requestAnimationFrame(() =>
                            autoResizeTextarea(el)
                          );
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        placeholder="No comments"
                      />
                    </div>
                  </div>
                  </div>
                </div>

                {/* 2) Screening connector — a wide arrow pointing into the screening card below */}
                <div className="col-12 col-md d-flex">
                  {/* Relative shell keeps the layout box; the arrow itself extends
                      28px past it so the tip enters the screening card below. */}
                  <div className="w-100 position-relative">
                  {/* drop-shadow lives on this un-clipped wrapper so it traces the
                      arrow silhouette and shows the tip floating inside the card below */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: -92,
                      zIndex: 2,
                      WebkitMaskImage:
                        "linear-gradient(to bottom, transparent 0px, #000 26px)",
                      maskImage:
                        "linear-gradient(to bottom, transparent 0px, #000 26px)",
                    }}
                  >
                  <div
                    className="w-100 d-flex flex-column"
                    style={{ height: "calc(100% - 24px)" }}
                  >
                    <div
                      className="d-flex flex-column align-items-center text-center flex-grow-1 position-relative"
                      style={{
                        width: "90%",
                        margin: "0 auto -1px",
                        zIndex: 1,
                        background: "#EDEAE9",
                        border: "1px solid #DCD3C7",
                        borderBottom: "none",
                        borderRadius: "10px 10px 0 0",
                        padding: "16px 8px 8px",
                      }}
                    >
                      <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            border: "1.5px solid rgba(125, 104, 86, 0.18)",
                            color: "rgba(125, 104, 86, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            fontWeight: 600,
                            pointerEvents: "none",
                          }}
                        >
                          2
                        </span>
                      <div
                        className="flex-grow-1 d-flex flex-column align-items-center justify-content-center"
                        style={{ gap: 10 }}
                      >
                        {screeningLoading ? (
                          <>
                            <div
                              className="appDotsLoader"
                              aria-hidden="true"
                              style={{ transform: "scale(0.6)", transformOrigin: "center" }}
                            />
                            <div style={{ color: "#A5ABB5", fontSize: 12, fontWeight: 500 }}>
                              Screening...
                            </div>
                          </>
                        ) : !hasResult ? (
                          <>
                            <div className="small text-muted">
                              {pepSubjects?.length ? (
                                <>
                                  Ready to screen {pepSubjects.length} name
                                  {pepSubjects.length > 1 ? "s" : ""}
                                </>
                              ) : (
                                "Missing name(s)"
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary kycPepSearchBtn"
                              style={{
                                width: 150,
                                minHeight: 38,
                                padding: "10px 18px",
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 3,
                              }}
                              onClick={runPepScreening}
                              disabled={readOnly || !pepSubjects?.length}
                              title={customerName ? "Run screening" : "Missing name"}
                            >
                              <img
                                src={doubleTapFullIcon}
                                alt=""
                                aria-hidden="true"
                                style={{ width: 32, height: 32, flex: "0 0 auto" }}
                              />
                              Search PEP
                            </button>
                          </>
                        ) : potentialCount > 0 && !isReviewCompleted ? null : potentialCount === 0 ? (
                          <div className="small text-muted">No matches found</div>
                        ) : null}
                      </div>
                    </div>
                    <svg
                          width="100%"
                          height="44"
                          viewBox="0 0 400 44"
                          preserveAspectRatio="none"
                          style={{ display: "block", flex: "0 0 auto", margin: "0 auto" }}
                          aria-hidden="true"
                        >
                          <path
                            d="M16 0 H384 C398 0 401 4 394 8.5 L216 39 C205 43.8 195 43.8 184 39 L6 8.5 C-1 4 2 0 16 0 Z"
                            fill="#EDEAE9"
                            stroke="#DCD3C7"
                            strokeWidth={1}
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                  </div>
                  </div>
                  </div>
                </div>

                {/* 3) Compliance assessment — same white 3D arrow dropping from
                    the Assessment tile into the card; card starts below the arrow.
                    flexBasis adds the Overall tile's 2/12 so the card also spans
                    below the PEP confirmed tile. */}
                <div
                  className="col-12 col-md d-flex"
                  style={{ flexBasis: "16.6666%" }}
                >
                  <div className="w-100 position-relative" style={{ paddingTop: 42 }}>
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "62.5%",
                        height: 80,
                        zIndex: 2,
                        WebkitMaskImage:
                          "linear-gradient(to bottom, transparent 0px, #000 26px)",
                        maskImage:
                          "linear-gradient(to bottom, transparent 0px, #000 26px)",
                      }}
                    >
                      <div className="w-100 position-relative" style={{ height: 60 }}>
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            top: 44,
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            border: "1.5px solid rgba(76, 84, 101, 0.16)",
                            color: "rgba(76, 84, 101, 0.26)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            fontWeight: 600,
                            pointerEvents: "none",
                          }}
                        >
                          3
                        </span>
                        <div
                          style={{
                            height: 22,
                            width: "90%",
                            margin: "0 auto -1px",
                            position: "relative",
                            zIndex: 1,
                            background: "#F4F5F7",
                            border: "1px solid #D9DCE1",
                            borderBottom: "none",
                            borderRadius: "10px 10px 0 0",
                          }}
                        />
                        <svg
                          width="100%"
                          height="44"
                          viewBox="0 0 400 44"
                          preserveAspectRatio="none"
                          style={{ display: "block", flex: "0 0 auto", margin: "0 auto" }}
                          aria-hidden="true"
                        >
                          <path
                            d="M16 0 H384 C398 0 401 4 394 8.5 L216 39 C205 43.8 195 43.8 184 39 L6 8.5 C-1 4 2 0 16 0 Z"
                            fill="#F4F5F7"
                            stroke="#D9DCE1"
                            strokeWidth={1}
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                      </div>
                    </div>
                  <div
                    className={`border rounded-3 h-100 d-flex flex-column w-100 position-relative ${
                      complianceAssessmentGlowActive
                        ? "kycComplianceAssessmentGlow"
                        : ""
                    }`}
                    onAnimationEnd={(e) => {
                      if (e.target !== e.currentTarget) return;
                      setComplianceAssessmentGlowActive(false);
                      setComplianceAssessmentIntroBackgroundActive(false);
                    }}
                    style={
                      shouldHighlightComplianceAssessment
                        ? {
                            padding: "40px 16px 16px",
                            background:
                              complianceAssessmentIntroBackgroundActive
                                ? "#FFFEF8"
                                : "#fff",
                            borderColor: "#DCD0A0",
                            boxShadow:
                              "inset 0 0 0 1px rgba(220, 208, 160, 0.18), 0 0 10px rgba(126, 111, 60, 0.06)",
                            transition:
                              "background-color 0.65s ease, box-shadow 0.25s ease",
                          }
                        : { padding: "40px 16px 16px", background: "#fff" }
                    }
                  >
                    {shouldHighlightComplianceAssessment &&
                      actionRequiredBadgeVisible && (
                      <span
                        className="position-absolute d-inline-flex flex-column align-items-center justify-content-center kycActionRequiredBadge"
                        title="Action required"
                        aria-label="Action required"
                        style={{
                          top: 12,
                          right: 12,
                          width: 70,
                          height: 70,
                          border: "1px solid #DCD0A0",
                          borderRadius: "50%",
                          padding: "7px 6px 8px",
                          gap: 2,
                          fontSize: 8,
                          fontWeight: 500,
                          lineHeight: 1,
                          color: "#6C757D",
                          cursor: "default",
                          textAlign: "center",
                          userSelect: "none",
                          zIndex: 1,
                        }}
                      >
                        <img
                          src={handFingerUpIcon}
                          alt=""
                          aria-hidden="true"
                          style={{
                            width: 22,
                            height: 22,
                            display: "block",
                            flex: "0 0 auto",
                          }}
                        />
                        <span
                          style={{
                            display: "block",
                            width: "100%",
                            marginTop: 2,
                          }}
                        >
                          Action required
                        </span>
                      </span>
                    )}
                    <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                      <div>
                        <div className="small text-muted">
                          Compliance assessment after final review
                        </div>
                      </div>
                    </div>

                    <div
                      style={
                        !complianceUsable
                          ? { visibility: "hidden", pointerEvents: "none" }
                          : undefined
                      }
                      className="flex-grow-1 d-flex flex-column"
                    >
                      <div className="mt-3">
                        <div className="row g-2 align-items-start">
                          {phase1 === "flagged" ? (
                            <div className="col-12">
                              <div
                                className="btn-group flex-wrap w-100"
                                role="group"
                              >
                                <button
                                  type="button"
                                  className={`btn btn-sm ${
                                    phase2 === "flagged_remediated"
                                      ? "btn-toggle-on-lightgrey"
                                      : "btn-toggle-off-lightgrey"
                                  } ${cc(["pep", "staffDecision"])}`}
                                  disabled={readOnly}
                                  onClick={() =>
                                    setPhase2("flagged_remediated")
                                  }
                                >
                                  Remediated
                                </button>

                                <button
                                  type="button"
                                  className={`btn btn-sm ${
                                    phase2 === "flagged_confirmed"
                                      ? "btn-toggle-on-lightgrey"
                                      : "btn-toggle-off-lightgrey"
                                  } ${cc(["pep", "staffDecision"])}`}
                                  disabled={readOnly}
                                  onClick={() => setPhase2("flagged_confirmed")}
                                >
                                  Confirmed
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="mt-3">
                        <div
                          className="text-muted small"
                          style={{ marginTop: 5, marginBottom: 8 }}
                        >
                          Compliance comments
                        </div>
                        <textarea
                          ref={complianceCommentsRef}
                          className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                            "pep",
                            "staffAssessment",
                          ])}`}
                          rows={1}
                          disabled={readOnly}
                          value={pep?.staffAssessment || ""}
                          onChange={(e) => {
                            const el = e.currentTarget;
                            writeAt?.(
                              ["pep", "staffAssessment"],
                              e.target.value
                            );
                            window.requestAnimationFrame(() =>
                              autoResizeTextarea(el)
                            );
                          }}
                          onInput={(e) => autoResizeTextarea(e.currentTarget)}
                          placeholder="No comment (optional)"
                        />
                      </div>
                    </div>

                  </div>
                  </div>
                </div>

                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Screening results card — outside the band; the arrow's tip lands inside it */}
                {/* 4) Screening results card (✅ animated entrance + layout) */}
                <AnimatePresence initial={false}>
                  {showDetails && hasResult ? (
                    <motion.div
                      key="pep-screening-card"
                      style={{ marginTop: 8 }}
                      {...cardIn}
                      layout
                    >
                      <div
                        className="p-3 border rounded-3 mb-3"
                        // F5F3F2 FCFCFF
                      >
                        <div
                          className="p-3 rounded-3 border"
                          style={{
                            background: styles.bg,
                            borderColor: styles.border,
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ color: styles.text }}
                              >
                                PEP screening
                              </div>

                              {pepSubjects?.length ? (
                                <div
                                  className="small text-muted mt-2"
                                  style={{ opacity: 0.9 }}
                                >
                                  Screened {pepSubjects.length}{" "}
                                  {pluralize(
                                    pepSubjects.length,
                                    "name",
                                    "names"
                                  )}{" "}
                                  <span className="text-muted">
                                    ({screenedRolesText(pepSubjects)})
                                  </span>
                                </div>
                              ) : null}

                              <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                                {uniqueMatches.length ? (
                                  <span
                                    className="badge badgeStandard"
                                    style={{
                                      background: "#B7B9BF",
                                      color: "#fff",
                                    }}
                                  >
                                    {uniqueMatches.length} potential matches
                                  </span>
                                ) : null}

                                {uniqueMatches.length === 0 ? (
                                  badge("No matches found", "success")
                                ) : isReviewCompleted ? (
                                  savedCount > 0 ? (
                                    <span
                                      className="badge badgeStandard"
                                      style={{
                                        background: "#D2B1B9",
                                        color: "#fff",
                                      }}
                                    >
                                      {savedCount} confirmed match
                                      {savedCount > 1 ? "es" : ""}
                                    </span>
                                  ) : (
                                    badge("All hits cleared", "success")
                                  )
                                ) : null}
                              </div>
                              {/* {pepSubjects?.length ? (
                          <div
                            className="small text-muted mt-1"
                            style={{ opacity: 0.9 }}
                          >
                            Screened {pepSubjects.length} name
                            {pepSubjects.length > 1 ? "s" : ""} ·{" "}
                            {isEntity
                              ? "client + ownership/control persons"
                              : "client only"}
                          </div>
                        ) : null} */}

                              {Array.isArray(screeningResult?.errors) &&
                              screeningResult.errors.length ? (
                                <div
                                  className="small text-muted mt-1"
                                  style={{ color: "#a36b6b" }}
                                >
                                  {screeningResult.errors.length} name
                                  {screeningResult.errors.length > 1
                                    ? "s"
                                    : ""}{" "}
                                  failed to screen (still showing results for
                                  other names).
                                </div>
                              ) : null}
                            </div>

                            <div className="d-flex flex-column align-items-end gap-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                {uniqueMatches.length ? (
                                  selectedOnly ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-lightBlue"
                                      disabled={readOnly}
                                      onClick={editSelection}
                                    >
                                      Edit selection
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-lightBlue"
                                        disabled={readOnly}
                                        onClick={saveSelectionCompleted}
                                        title={
                                          draftCount === 0
                                            ? "Mark as reviewed (no matches confirmed)"
                                            : "Mark as reviewed"
                                        }
                                      >
                                        Mark as reviewed
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        disabled={readOnly}
                                        onClick={saveSelectionDraft}
                                        title="Save draft (review not completed)"
                                      >
                                        Save draft
                                      </button>
                                    </>
                                  )
                                ) : null}

                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={runPepScreening}
                                  disabled={readOnly || screeningLoading}
                                  title="Re-run screening"
                                >
                                  <i className="bi bi-arrow-repeat" />
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={clearScreening}
                                  disabled={readOnly || screeningLoading}
                                  title="Clear results"
                                >
                                  <i className="bi bi-x-lg" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {groupedPepReviewItems.length ? (
                          <>
                            <div
                              className="mt-3"
                              onScroll={(event) => {
                                if (!pepReviewHeaderSticky) {
                                  setPepReviewScrolled(false);
                                  return;
                                }
                                const next = event.currentTarget.scrollTop > 2;
                                setPepReviewScrolled((prev) =>
                                  prev === next ? prev : next
                                );
                              }}
                              style={{
                                maxHeight: 560,
                                overflowY: "auto",
                              }}
                            >
                              <motion.div
                                className="d-grid"
                                style={{ gap: 12 }}
                                layout
                                transition={T.med}
                              >
                                <AnimatePresence initial={false}>
                                  {groupedPepReviewItems.map((group, idx) => {
                                    const aiItem = group.aiItem;
                                    const assessmentText = String(
                                      aiItem?.result?.assessment || ""
                                    ).trim();
                                    const signalLabel =
                                      consolidatedPepSignalLabel(
                                        aiItem,
                                        group.matches
                                      );
                                    const contextIcon = pepWebContextIcon(
                                      aiItem?.result?.outcome
                                    );

                                    return (
                                      <motion.div
                                        key={group.key}
                                        className="rounded-3"
                                        layout
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{
                                          opacity: 1,
                                          y: 0,
                                          transition: {
                                            ...T.fast,
                                            delay: Math.min(idx, 8) * 0.015,
                                          },
                                        }}
                                        exit={{ opacity: 0, y: 4, transition: T.fast }}
                                        style={{
                                          background: "#F9F7F5",
                                          color: "#4C5465",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: pepReviewHeaderSticky
                                              ? "sticky"
                                              : "relative",
                                            top: 0,
                                            zIndex: pepReviewHeaderSticky ? 3 : 1,
                                            background: "transparent",
                                          }}
                                        >
                                          {pepReviewHeaderSticky ? (
                                            <>
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  position: "absolute",
                                                  top: 0,
                                                  left: 0,
                                                  width: 18,
                                                  height: 18,
                                                  background: "#fff",
                                                  pointerEvents: "none",
                                                  zIndex: 0,
                                                }}
                                              />
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  position: "absolute",
                                                  top: 0,
                                                  right: 0,
                                                  width: 18,
                                                  height: 18,
                                                  background: "#fff",
                                                  pointerEvents: "none",
                                                  zIndex: 0,
                                                }}
                                              />
                                            </>
                                          ) : null}
                                          <div
                                            className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                                            style={{
                                              position: "relative",
                                              zIndex: 1,
                                              margin: 0,
                                              padding: "16px",
                                              background: pepReviewHeaderGlass
                                                ? "rgba(249, 247, 245, 0.72)"
                                                : "#F9F7F5",
                                              backdropFilter: pepReviewHeaderGlass
                                                ? "blur(16px) saturate(1.14)"
                                                : "none",
                                              WebkitBackdropFilter:
                                                pepReviewHeaderGlass
                                                  ? "blur(16px) saturate(1.14)"
                                                  : "none",
                                              borderTop:
                                                pepReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderLeft:
                                                pepReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderRight:
                                                pepReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderTopLeftRadius: 8,
                                              borderTopRightRadius: 8,
                                              boxShadow: pepReviewHeaderGlass
                                                ? "0 10px 26px rgba(45, 50, 60, 0.08)"
                                                : "none",
                                              transition:
                                                "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
                                            }}
                                          >
                                          <div
                                            className="d-flex gap-2"
                                            style={{
                                              alignItems: "center",
                                              minHeight: 58,
                                            }}
                                          >
                                            <span
                                              aria-hidden="true"
                                              className="d-inline-flex align-items-center justify-content-center"
                                              style={{
                                                width: 52,
                                                height: 58,
                                                flex: "0 0 52px",
                                                overflow: "visible",
                                              }}
                                            >
                                              <img
                                                src={contextIcon}
                                                alt=""
                                                aria-hidden="true"
                                                style={{
                                                  width: 50,
                                                  height: 50,
                                                  display: "block",
                                                  opacity: 1,
                                                  transform: "translateY(2px)",
                                                  transformOrigin: "center",
                                                }}
                                              />
                                            </span>
                                            <div
                                              className="d-flex flex-column justify-content-center"
                                              style={{ minHeight: 58 }}
                                            >
                                              <div
                                                className="fw-semibold"
                                                style={{ color: "#2f3441" }}
                                              >
                                                {toTitleCaseWords(group.name) ||
                                                  "Subject"}
                                              </div>

                                              {group.roles?.length ? (
                                                <div className="d-flex gap-2 flex-wrap mt-2">
                                                  {group.roles.map((role) => (
                                                    <React.Fragment key={role}>
                                                      {roleBadge(role)}
                                                    </React.Fragment>
                                                  ))}
                                                </div>
                                              ) : null}
                                            </div>
                                          </div>

                                          <div className="d-flex gap-2 flex-wrap pepWebAssessmentBadges justify-content-end">
                                            {pepSignalBadge(signalLabel)}
                                            {aiItem
                                              ? pepWebContextMatchBadge(
                                                  aiItem?.result?.outcome
                                                )
                                              : pepWebAssessmentLoading
                                              ? badge("Assessing", "grey")
                                              : pepWebAssessmentErr
                                              ? badge(
                                                  "Assessment unavailable",
                                                  "grey"
                                                )
                                              : group.matches.length
                                              ? badge("Review needed", "grey")
                                            : null}
                                          </div>
                                        </div>
                                        </div>

                                        <div
                                          style={{
                                            marginTop: 0,
                                            padding: "0 16px 16px",
                                            borderLeft: "1px solid #DEE2E6",
                                            borderRight: "1px solid #DEE2E6",
                                            borderBottom: "1px solid #DEE2E6",
                                            borderBottomLeftRadius: 8,
                                            borderBottomRightRadius: 8,
                                            background: "#F9F7F5",
                                          }}
                                        >
                                        <div
                                          className="rounded-3 border bg-white p-3"
                                          style={{ marginBottom: 12 }}
                                        >
                                          <div
                                            className="fw-semibold"
                                            style={{
                                              color: "#4C5465",
                                              fontSize: 13,
                                              marginBottom: 6,
                                            }}
                                          >
                                            Assessment
                                          </div>

                                          <div
                                            className="small"
                                            style={{
                                              color: "#4C5465",
                                              whiteSpace: "pre-wrap",
                                              lineHeight: 1.5,
                                            }}
                                          >
                                            {assessmentText ? (
                                              assessmentText
                                            ) : pepWebAssessmentLoading ? (
                                              <span className="d-inline-flex align-items-center gap-2 text-muted">
                                                <span
                                                  className="spinner-border spinner-border-sm"
                                                  role="status"
                                                  aria-hidden="true"
                                                />
                                                <span>
                                                  Researching public web
                                                  sources…
                                                </span>
                                              </span>
                                            ) : pepWebAssessmentErr ? (
                                              <span className="text-danger">
                                                {pepWebAssessmentErr}
                                              </span>
                                            ) : group.matches.length ? (
                                              "Potential match identified. Review the match details below."
                                            ) : (
                                              "No public assessment available."
                                            )}
                                          </div>
                                        </div>

                                        <div>
                                          <div
                                            className="fw-semibold"
                                            style={{
                                              color: "#4C5465",
                                              fontSize: 13,
                                              marginBottom: 8,
                                            }}
                                          >
                                            Potential matches
                                          </div>

                                          {group.matches.length ? (
                                            <motion.div
                                              className="list-group"
                                              layout
                                              transition={T.med}
                                            >
                                              <AnimatePresence initial={false}>
                                                {group.matches.map(
                                                  (match, matchIdx) =>
                                                    renderPepMatchRow(
                                                      match,
                                                      matchIdx
                                                    )
                                                )}
                                              </AnimatePresence>
                                            </motion.div>
                                          ) : (
                                            <div className="small text-muted">
                                              No database match identified.
                                            </div>
                                          )}
                                        </div>
                                        </div>
                                      </motion.div>
                                    );
                                  })}
                                </AnimatePresence>
                              </motion.div>
                            </div>

                            {uniqueMatches.length ? (
                              <motion.div
                              className="d-flex align-items-center gap-2 flex-wrap mt-3"
                              layout
                              transition={T.med}
                            >
                              <div className="small text-muted">
                                {selectedOnly ? (
                                  <>
                                    {isReviewCompleted ? (
                                      <>
                                        Marked as reviewed{" "}
                                        <span className="fw-semibold">
                                          ({selectedKeys.size} confirmed)
                                        </span>
                                        .
                                      </>
                                    ) : isReviewDraft ? (
                                      <>
                                        Draft saved{" "}
                                        <span className="fw-semibold">
                                          ({selectedKeys.size} confirmed)
                                        </span>
                                        .
                                      </>
                                    ) : (
                                      <>
                                        Saved{" "}
                                        <span className="fw-semibold">
                                          ({selectedKeys.size} confirmed)
                                        </span>
                                        .
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    Confirm matches, then save{" "}
                                    <span className="fw-semibold">
                                      ({draftCount})
                                    </span>
                                    .
                                  </>
                                )}
                              </div>
                              </motion.div>
                            ) : null}
                          </>
                        ) : isReviewCompleted && selectedKeys.size === 0 ? null : (
                          <div className="small mt-3">No matches found.</div>
                        )}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
        {/* --- END --- */}
      </div>
      {/* -- end main card -- */}
    </div>
  );
});

export default KycSectionPepBis2;
